/**
 * InstagramProfileModal - Modal for Instagram profile setup
 *
 * Allows the user to enter their Instagram username and automatically
 * fetches profile data (name, avatar, bio, stats) via the backend
 * using Apify's instagram-profile-scraper.
 *
 * When the automatic fetch fails (e.g. age-restricted/blocked profiles that
 * Instagram won't serve to a logged-out scraper), the user can switch to a
 * MANUAL entry mode and type their name, @handle, bio and upload a photo.
 * Both paths persist to cai_profiles in Supabase via the same save flow.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { Profile } from '@/types';
import { fetchInstagramProfile, InstagramProfileData } from '@/services/instagramService';
import { getProfile, saveProfile, ProfilePreferences, UserProfile } from '@/services/profileService';
import { apiPost } from '@/services/apiClient';
import { trackEvent } from '@/services/analyticsService';
import { blobToDataUrl, downscaleImageDataUrl } from '@/lib/imageCompression';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Loader2, Search, CheckCircle, Instagram, Users, FileText, BadgeCheck, X, Lock, Upload, PencilLine } from 'lucide-react';

interface InstagramProfileModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the modal closes (after save or cancel) */
  onClose: () => void;
  /** Called with the saved profile data after successful save */
  onProfileSaved: (profile: UserProfile) => void;
  /** If true, the user cannot dismiss the modal (first-time setup) */
  required?: boolean;
  /** The authenticated user's ID (from useAuth()) */
  userId: string;
}

const InstagramProfileModal: React.FC<InstagramProfileModalProps> = ({
  open,
  onClose,
  onProfileSaved,
  required = false,
  userId,
}) => {
  const { t } = useTranslation();
  const { status, refreshStatus } = useUserStatus();
  const isHandleLocked = !!status?.instagramLocked;
  const [username, setUsername] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<InstagramProfileData | null>(null);

  // Manual entry fallback (used when the automatic scrape fails/blocks)
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualBio, setManualBio] = useState('');
  const [manualAvatar, setManualAvatar] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;

    let active = true;

    const loadSavedProfile = async () => {
      try {
        const currentProfile = await getProfile(userId);
        if (!active || !currentProfile?.handle?.trim()) return;

        const currentPrefs: ProfilePreferences = currentProfile?.preferences || {};

        setUsername(currentProfile.handle);
        setProfileData({
          fullName: currentProfile.name || currentProfile.handle,
          username: currentProfile.handle,
          biography: currentPrefs.biography || '',
          profilePicUrl: currentProfile.avatarUrl || '',
          profilePicUrlHD: currentProfile.avatarUrl || '',
          avatarBase64: currentProfile.avatarUrl?.startsWith('data:') ? currentProfile.avatarUrl : null,
          followersCount: currentPrefs.followersCount || 0,
          followsCount: currentPrefs.followsCount || 0,
          postsCount: currentPrefs.postsCount || 0,
          verified: !!currentPrefs.verified,
          isBusinessAccount: !!currentPrefs.isBusinessAccount,
          externalUrl: currentPrefs.externalUrl || '',
        });
      } catch (err) {
        // Failed to preload saved Instagram profile - non-blocking
      }
    };

    loadSavedProfile();

    return () => {
      active = false;
    };
  }, [open, userId]);

  const handleSearch = async () => {
    const cleanUsername = username.replace(/^@/, '').trim();
    if (!cleanUsername) {
      setError(t('instagram.invalidUsername'));
      return;
    }

    setIsSearching(true);
    setError(null);
    setProfileData(null);

    try {
      const data = await fetchInstagramProfile(cleanUsername);
      // Some profiles (age-restricted/blocked) come back with no usable data.
      // Treat an empty/blocked result as a failure so the manual fallback shows.
      if (!data || (!data.fullName && !data.profilePicUrl && !data.profilePicUrlHD && !data.biography && !data.followersCount && !data.postsCount)) {
        throw new Error('blocked');
      }
      setProfileData(data);
    } catch (err: any) {
      // Profile fetch failed (timeout, blocked, age-restricted, not found).
      setProfileData(null);
      setError('Não consegui buscar esse perfil automaticamente (alguns perfis são bloqueados pelo Instagram). Você pode preencher manualmente abaixo.');
    } finally {
      setIsSearching(false);
    }
  };

  /** Build a profile object from the manual-entry fields. */
  const buildManualData = (): InstagramProfileData => {
    const handle = username.replace(/^@/, '').trim();
    return {
      fullName: manualName.trim() || handle,
      username: handle,
      biography: manualBio.trim(),
      profilePicUrl: '',
      profilePicUrlHD: '',
      avatarBase64: manualAvatar,
      followersCount: 0,
      followsCount: 0,
      postsCount: 0,
      verified: false,
      isBusinessAccount: false,
      externalUrl: '',
    };
  };

  /** Switch to manual entry, seeding fields from any partial scrape result. */
  const enterManualMode = () => {
    setError(null);
    setManualName((prev) => prev || profileData?.fullName || '');
    setManualBio((prev) => prev || profileData?.biography || '');
    setManualAvatar((prev) => prev || profileData?.avatarBase64 || null);
    setManualMode(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem.');
      return;
    }
    setPhotoLoading(true);
    setError(null);
    try {
      const dataUrl = await blobToDataUrl(file);
      const compressed = await downscaleImageDataUrl(dataUrl, 400, 0.8);
      setManualAvatar(compressed);
    } catch {
      setError('Não consegui processar a imagem. Tente outra.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSave = async () => {
    const data = manualMode ? buildManualData() : profileData;
    if (!data) return;
    if (!data.username) {
      setError('Informe o @ do seu Instagram.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Lock the Instagram handle FIRST (anti-abuse: 1 IG per account).
      // If the handle is taken by another user, block before saving profile.
      if (!isHandleLocked) {
        try {
          await apiPost('/api/profile/instagram', { handle: data.username });
          trackEvent('ig_linked', { handle: data.username });
          await refreshStatus();
        } catch (lockErr: any) {
          if (lockErr?.code === 'INSTAGRAM_HANDLE_TAKEN') {
            setError(`O Instagram @${data.username} ja esta vinculado a outra conta. Use outro perfil.`);
            setIsSaving(false);
            return;
          }
          // INSTAGRAM_ALREADY_LOCKED is acceptable — handle was already locked to this user
          if (lockErr?.code !== 'INSTAGRAM_ALREADY_LOCKED') {
            console.warn('Failed to lock Instagram handle:', lockErr);
          }
        }
      }

      // Handle is available (or already locked to this user) — save profile
      const currentProfile = await getProfile(userId);
      const currentPrefs: ProfilePreferences = currentProfile?.preferences || {};

      const profile: Profile = {
        name: data.fullName || data.username,
        handle: data.username,
        avatarUrl: data.avatarBase64 || data.profilePicUrlHD || data.profilePicUrl,
      };

      const preferences: ProfilePreferences = {
        biography: data.biography,
        followersCount: data.followersCount,
        followsCount: data.followsCount,
        postsCount: data.postsCount,
        verified: data.verified,
        isBusinessAccount: data.isBusinessAccount,
        externalUrl: data.externalUrl,
      };

      const savedProfile = await saveProfile(userId, profile, {
        ...currentPrefs,
        ...preferences,
      });

      onProfileSaved(savedProfile);
      onClose();
    } catch (err: any) {
      // Profile save failed - error shown to user via setError
      setError(err.message || t('instagram.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching && username.trim()) {
      handleSearch();
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toString();
  };

  const canConfirm = manualMode
    ? !!(manualName.trim() && username.replace(/^@/, '').trim())
    : !!profileData;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl w-full max-w-md shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                <Instagram className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{t('instagram.title')}</h2>
                <p className="text-xs text-muted-foreground">
                  {required ? t('instagram.subtitleRequired') : t('instagram.subtitleOptional')}
                </p>
              </div>
            </div>
            {!required && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Username input */}
          <div className="space-y-2">
            <Label htmlFor="ig-username" className="text-sm font-medium">
              {t('instagram.usernameLabel')}
            </Label>
            {isHandleLocked && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2">
                <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Conta vinculada a <strong>@{username || status?.instagramHandle}</strong></span>
              </div>
            )}
            <div className="flex gap-2">
              <div className="flex flex-1">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm">
                  @
                </span>
                <Input
                  id="ig-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('instagram.usernamePlaceholder')}
                  className="rounded-l-none"
                  disabled={isSearching || isHandleLocked}
                />
              </div>
              {!isHandleLocked && !manualMode && (
                <Button
                  onClick={handleSearch}
                  disabled={isSearching || !username.trim()}
                  className="px-4"
                >
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            {/* Manual-entry escape hatch — always available (some profiles can't be scraped) */}
            {!isHandleLocked && !manualMode && !isSearching && (
              <button
                type="button"
                onClick={enterManualMode}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                <PencilLine className="h-3.5 w-3.5" />
                Não encontrou seu perfil? Preencher manualmente
              </button>
            )}
          </div>

          {/* Loading state */}
          {isSearching && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="text-sm font-medium">{t('instagram.searching')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('instagram.searchingHint')}
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 space-y-2">
              <p className="text-sm text-destructive">{error}</p>
              {!manualMode && !isHandleLocked && (
                <button
                  type="button"
                  onClick={enterManualMode}
                  className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                >
                  <PencilLine className="h-3.5 w-3.5" />
                  Preencher perfil manualmente
                </button>
              )}
            </div>
          )}

          {/* Manual entry form */}
          {manualMode && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Preencher manualmente</p>
                {!isSearching && (
                  <button
                    type="button"
                    onClick={() => setManualMode(false)}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    Voltar à busca
                  </button>
                )}
              </div>

              {/* Photo */}
              <div className="flex items-center gap-4">
                {manualAvatar ? (
                  <img
                    src={manualAvatar}
                    alt="Avatar"
                    className="w-16 h-16 rounded-full object-cover border-2 border-border flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted border-2 border-border flex items-center justify-center text-muted-foreground flex-shrink-0">
                    <Instagram className="h-6 w-6" />
                  </div>
                )}
                <label className={cn('cursor-pointer', photoLoading && 'pointer-events-none opacity-60')}>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={photoLoading}
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-muted text-sm hover:bg-accent transition-colors">
                    {photoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {manualAvatar ? 'Trocar foto' : 'Enviar foto'}
                  </span>
                </label>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="ig-name" className="text-sm font-medium">Nome</Label>
                <Input
                  id="ig-name"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="Seu nome (como aparece no perfil)"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="ig-bio" className="text-sm font-medium">
                  Bio <span className="text-muted-foreground font-normal">(opcional)</span>
                </Label>
                <textarea
                  id="ig-bio"
                  value={manualBio}
                  onChange={(e) => setManualBio(e.target.value)}
                  rows={3}
                  placeholder="Em uma frase, o que você faz e pra quem"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}

          {/* Profile preview (automatic scrape result) */}
          {profileData && !isSearching && !manualMode && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {/* Profile card */}
              <div className="bg-muted/50 border border-border rounded-xl p-4">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <img
                    src={profileData.avatarBase64 || profileData.profilePicUrlHD || profileData.profilePicUrl}
                    alt={profileData.username}
                    className="w-16 h-16 rounded-full object-cover border-2 border-border flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-bold text-sm truncate">
                        {profileData.fullName || profileData.username}
                      </h3>
                      {profileData.verified && (
                        <BadgeCheck className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">@{profileData.username}</p>
                    {profileData.biography && (
                      <p className="text-xs text-foreground/80 mt-1.5 line-clamp-2">
                        {profileData.biography}
                      </p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border">
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-semibold">{formatNumber(profileData.postsCount)}</span>
                    <span className="text-xs text-muted-foreground">{t('instagram.posts')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-semibold">{formatNumber(profileData.followersCount)}</span>
                    <span className="text-xs text-muted-foreground">{t('instagram.followers')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold">{formatNumber(profileData.followsCount)}</span>
                    <span className="text-xs text-muted-foreground">{t('instagram.following')}</span>
                  </div>
                </div>
              </div>

              {/* Confirmation info */}
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                <p>{t('instagram.confirmMessage')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          {!required && (
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!canConfirm || isSaving || isSearching || photoLoading}
            className={cn("flex-1", required && "w-full")}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('instagram.saving')}
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('instagram.confirmProfile')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstagramProfileModal;
